import autoComplete from 'autocomplete.js';
import Base from '../../../js/_base';
import {keyCodes, isIE} from '../../../js/_utlis';

class QuickSearch extends Base {
    activeDropdownJournal;

    get selectors() {
        return {
            qsElements: '.quick-search__input',
            qsAutocomplete: 'input.autocomplete',
            qsForms: '[name="defaultQuickSearch"],[name="thisJournalQuickSearch"]',
            qsSeriesKey: '[name=SeriesKey]',
            qsDropdown: '.quick-search__dropdown a',
            qsHeaderWrapper: '.quick-search--header',
            qsItems: '.quick-search__items',
        };
    }

    get elements() {
        return {
            qsElements: null,
            qsForms: null,
            qsDropdowns: null,
            qsHeaderWrapper: null,
            qsAutocomplete: null,
        };
    }

    // On/Off Autocomplete
    get isAutocompleteOn() {
        return true;
    }

    // Submit quick search if user pressed Enter without choosing an autocomplete value
    get submitWithoutChoosingValue() {
        return false;
    }

    // Reserve the user input when changing between different journals
    get reserveSearchInputOnJournalChange() {
        return false;
    }

    get activeJournalForm() {
        return this._elements.qsHeaderWrapper?.querySelector(`[aria-labelledby="${this.activeDropdownJournal}"] form`);
    }

    constructor() {
        super();
        this.URLParameters = {
            hs: 'history-items-conf',
            pts: 'publication-titles-conf',
            ptgs: 'group-titles-conf',
            ptfs: 'publication-items-conf',
            ts: 'topics-conf',
            cs: 'contributors-conf',
        };
        this.initialize();
    }

    initialize() {
        this.setElements();
        // Set activeDropdownJournal to default "Anywhere"
        this.activeDropdownJournal = document
            .querySelector(`${this._selectors.qsItems} > div`)
            ?.getAttribute('aria-labelledby')
            ?.replace(/[#]/, '');
        if (this._elements.qsElements.length && this.isAutocompleteOn && !isIE()) {
            this.initAutocomplete();
        }
        this.addEventListeners();
    }

    setElements() {
        this._elements.qsElements = document.querySelectorAll(this._selectors.qsElements);
        this._elements.qsForms = document.querySelectorAll(this._selectors.qsForms);
        this._elements.qsDropdowns = document.querySelectorAll(this._selectors.qsDropdown);
        this._elements.qsAutocomplete = document.querySelectorAll(this._selectors.qsAutocomplete);
        this._elements.qsHeaderWrapper = document.querySelector(this._selectors.qsHeaderWrapper);
    }

    initAutocomplete() {
        this.autcompleteInstances = new Map();
        this._elements.qsAutocomplete.forEach((elem, i) => {
            // check if the search element is in a specific journal or anywhere and update it accordingly
            if (elem.parentNode.classList.contains('option-journal')) {
                elem.searchType = 'journal';
                elem.seriesKey = elem.parentNode.querySelector(this._selectors.qsSeriesKey)?.value;
            }
            this.autcompleteInstances.set(
                elem,
                new autoComplete({
                    data: {
                        src: async () => {
                            const url = this.buildFetchURL(elem);
                            if (url) {
                                const data = await fetch(url);
                                return await data.json();
                            } else {
                                return [];
                            }
                        },
                        cache: false,
                    },
                    selector: () => elem,
                    searchEngine: (query, record) => {
                        return record.highlight || record.label;
                    },
                    resultItem: {
                        content: (data, source) => {
                            const a = document.createElement('a');
                            a.href = data.url;
                            a.innerHTML = `<span>${data.label}</span>`;
                            if (data.paramFormatted) {
                                a.innerHTML += `<span class='autoComplete__category'>${data.paramFormatted}</span>`;
                            }
                            source.innerHTML = '';
                            source.appendChild(a);
                        },
                    },
                    resultsList: {
                        render: true,
                        idName: 'autoComplete-' + i,
                        className: 'autoComplete list-unstyled',
                        destination: elem.parentElement.parentElement,
                    },
                    maxResults: 10,
                    highlight: false,
                    sort: false,
                    threshold: 2,
                    debounce: 300,
                    onSelection: feedback => {
                        window.location.href = feedback.selection.value.url;
                    },
                })
            );
        });

        this.autcompleteInstances.forEach((autocomplete, input, i) => {
            input.setAttribute('type', 'search');
            // buggy className options in the plugin - needs to be done in the post processing
            input.addEventListener('autoComplete.rendered', () => {
                window.addEventListener(
                    'scroll',
                    e => {
                        autocomplete.closeAllList();
                    },
                    {once: true}
                );
            });
        });
    }

    addEventListeners() {
        super.addEventListeners();
        if (this.isAutocompleteOn && this.submitWithoutChoosingValue && !isIE()) {
            if (this._elements.qsElements && this._elements.qsElements.length > 0) {
                this._elements.qsElements.forEach(input => {
                    input.addEventListener('keydown', e => {
                        if (e.keyCode === keyCodes.RETURN && input.value !== '') {
                            if (!input.dataset.ariaActivedescendant) {
                                input.closest('form').submit();
                            }
                        }
                    });
                });
            }
        }

        this._elements.qsForms.forEach(form => {
            form.addEventListener('submit', this.onFormSubmit.bind(this, form));
        });

        if (this._elements.qsDropdowns && this._elements.qsDropdowns.length > 0) {
            this._elements.qsDropdowns.forEach(item => {
                item.addEventListener('click', this.handleDropdownCollapse.bind(this, item));
            });
        }
    }

    onFormSubmit(form, e) {
        const inputValue = form.querySelector(this._selectors.qsElements)?.value;
        if (inputValue === '') {
            window.location = '/search/advanced';
            e.preventDefault();
        }
    }

    handleDropdownCollapse(item, e) {
        const selectedItemId = item.id;
        if (this.activeDropdownJournal === selectedItemId) {
            // prevent collapse default behavior of collapsing the menu when pressing on it again
            item.removeAttribute('data-toggle');
            setTimeout(() => {
                item.setAttribute('data-toggle', 'collapse');
            }, 0);
        } else {
            if (this.reserveSearchInputOnJournalChange) {
                if (this.activeJournalForm) {
                    let inputValue = this.activeJournalForm.querySelector(this._selectors.qsElements).value;
                    this.activeDropdownJournal = selectedItemId;
                    this.activeJournalForm.querySelector(this._selectors.qsElements).value = inputValue;
                }
            } else {
                this.activeDropdownJournal = selectedItemId;
            }
        }
    }

    buildFetchURL(input) {
        const parameters = Object.keys(this.URLParameters).reduce((acc, key) => {
            const value = input.dataset[key];
            if (value) {
                acc.push(`${this.URLParameters[key]}=${value}`);
            }
            return acc;
        }, []);
        const term = input.value.trim().replace(/\s\s+/g, ' ');
        if (
            term.split(' ').length >= input.dataset.autoCompleteMaxWords ||
            term.length >= input.dataset.autoCompleteMaxChars
        ) {
            return '';
        }
        // title-auto-complete maybe should be used for citation but it hasn't been used in literatum for quite a while
        return `/action/doSuggest?target=auto-complete&query=${encodeURIComponent(term)}&${parameters.join(
            '&'
        )}&fl=PubID${input.searchType === 'journal' ? '&within=' + input.seriesKey : ''}`;
    }
}

export default QuickSearch;
